import Layout from "modules/layout/dashboard"

import React, { useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button, Flex, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import PageHeader from "modules/layout/dashboard/main/page-header"
import type { IActivityIncomeForm } from "./types"
import { useActivityIncomeTracking, useCreateActivityIncome } from "./queries"
import { ISession, useExtSessions } from "pages/assignations/preassignations/queries"
import { setFormData } from "utils/form-data"
import { useActivityIncomeId } from ".."
import ActivityIncomeForm from "./form"
import type { ActivityIncomeFormRef } from "./form"
import Loader from "components/loader"

const { Main, Header } = Layout
const { Title } = Typography

const DuplicateActivityIncome: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const formRef = useRef<ActivityIncomeFormRef>(null)

  const activityIncomeId = useActivityIncomeId()

  const activityIncome = useActivityIncomeTracking(activityIncomeId!, {
    cacheTime: 0,
  })

  const duplicateActivityIncome = useCreateActivityIncome()

  const sessions = useExtSessions(undefined, {
    select(data) {
      return searchParams.get("session")
        ? data.filter((session) => session.id === searchParams.get("session"))
        : ([
            data.find((session) => session.status === "progress") ||
              data.find((session) => session.status === "published"),
          ] as ISession[])
    },
    onSuccess(data) {
      if (!searchParams.get("session")) {
        searchParams.set("session", data[0]?.id || "")
        setSearchParams(searchParams)
      }
    },
  })

  const onSubmit = async (data: IActivityIncomeForm) => {
    const formData = setFormData<IActivityIncomeForm>(data)

    if (typeof data.voucher === "string") {
      const res = await fetch(data.voucher)
      const blob = await res.blob()
      const fileName =
        new URL(data.voucher).pathname.split("/").pop() || "voucher"

      formData.delete("voucher")
      formData.append(
        "voucher",
        new File([blob], fileName, { type: blob.type })
      )
    }

    duplicateActivityIncome.mutate(formData, {
      onSuccess: () => {
        navigate("../..")
      },
      onError(error: any) {
        if (error.response.data?.code === "form/validation") {
          for (let key in error.response.data.errors) {
            formRef.current?.form.setError(key as any, {
              message: error.response.data.errors[key][0].message,
            })
          }
        }
      },
    })
  }

  return (
    <Layout>
      <Header />
      <Main>
        <Flex vertical style={{ margin: "16pt", flexGrow: 1 }} gap="middle">
          <PageHeader
            onButtonClick={() => navigate("../..")}
            title={
              <Title level={4}>
                {t("global.duplicate_X", {
                  entity: t("finance.income"),
                  context: "male",
                })}
                {!!sessions.data?.[0]?.name &&
                  ` (${t("labels.session")}: ${sessions.data?.[0].name})`}
              </Title>
            }
          />
          {activityIncome.isLoading ? (
            <Loader />
          ) : (
            <ActivityIncomeForm
              ref={formRef}
              onSubmit={onSubmit}
              defaultValues={
                activityIncome.data
                  ? {
                      post_id: activityIncome.data.post.id!,
                      activity_id: activityIncome.data.activity.id!,
                      amount: activityIncome.data.amount,
                      date: activityIncome.data.date,
                      voucher: activityIncome.data.voucher,
                      description: activityIncome.data.description,
                    }
                  : undefined
              }
              session={sessions.data?.[0]}
            >
              <>
                <Space>
                  <Button
                    loading={duplicateActivityIncome.isLoading}
                    disabled={duplicateActivityIncome.isLoading}
                    type="primary"
                    htmlType="submit"
                  >
                    {t("global.duplicate")}
                  </Button>
                  <Button
                    onClick={() => navigate("../..")}
                    disabled={duplicateActivityIncome.isLoading}
                  >
                    {t("global.cancel")}
                  </Button>
                </Space>
              </>
            </ActivityIncomeForm>
          )}
        </Flex>
      </Main>
    </Layout>
  )
}

export default DuplicateActivityIncome
